import React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { PartnerElement } from "../../../models/PartnersInBranch";
import RowPartnetDocuments from "./RowPartnetDocuments.tsx";
import { styled } from "@mui/material/styles";

const StyledTableContainer = styled(TableContainer)({
  boxShadow: "none",
  borderRadius: "0",
  background: "transparent",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: "0",
  },
});

const StyledTableHead = styled(TableHead)({
  "& .MuiTableRow-root": {
    background: "linear-gradient(135deg, #6B7280 0%, #4B5563 100%)",
    "& .MuiTableCell-root": {
      color: "#fff",
      fontWeight: "600",
      fontSize: "13px",
      padding: "12px 16px",
      borderBottom: "none",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      transition: "all 0.2s ease",
      background: "transparent !important",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.1) !important",
      },
    },
    "& .MuiTableCell-root:first-of-type": {
      borderTopLeftRadius: "12px",
    },
    "& .MuiTableCell-root:last-of-type": {
      borderTopRightRadius: "12px",
    },
  },
});

const StyledTableBody = styled(TableBody)({
  "& .MuiTableRow-root": {
    transition: "all 0.2s ease",
    background: "#fff",
    "&:hover": {
      background: "#f9fafb",
    },
    "& .MuiTableCell-root": {
      padding: "10px 16px",
      borderBottom: "1px solid #e5e7eb",
      fontSize: "13px",
      color: "#374151",
    },
  },
});

const SortIcon = ({ order }: { order: string }) => (
  <span style={styles.sortIconWrapper}>
    <i
      className={`fa-solid fa-sort-${order === "asc" ? "up" : "down"}`}
      style={styles.sortIcon}
    />
  </span>
);

const TableDocuments = ({ data }: { data: PartnerElement[] }) => {
  const [records, setRecords] = React.useState<PartnerElement[]>(data);
  const [column, setColumn] = React.useState<string>("name");
  const [order, setOrder] = React.useState<string>("desc");
  const [hoveredColumn, setHoveredColumn] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRecords(data);
    setSort("name");
  }, [data]);

  const getIconSort = () => {
    return <SortIcon order={order} />;
  };

  const setSort = (sort: string) => {
    const sortOrder =
      column !== sort ? "asc" : order === "asc" ? "desc" : "asc";

    setColumn(sort);
    setOrder(sortOrder);

    if (sort === "name") {
      setRecords(
        data.sort((a, b) =>
          sortOrder === "asc"
            ? a.partner.person.firstName.localeCompare(
                b.partner.person.firstName
              )
            : b.partner.person.firstName.localeCompare(
                a.partner.person.firstName
              )
        )
      );
    }

    if (sort === "position") {
      setRecords(
        data.sort((a, b) =>
          sortOrder === "asc"
            ? a.partner.candidate.vacancy.position.name.localeCompare(
                b.partner.candidate.vacancy.position.name
              )
            : b.partner.candidate.vacancy.position.name.localeCompare(
                a.partner.candidate.vacancy.position.name
              )
        )
      );
    }

    if (sort === "recruitmentDate") {
      setRecords(
        data.sort((a, b) =>
          sortOrder === "asc"
            ? new Date(a.recruitmentDate).getTime() -
              new Date(b.recruitmentDate).getTime()
            : new Date(b.recruitmentDate).getTime() -
              new Date(a.recruitmentDate).getTime()
        )
      );
    }

    if (sort === "credentials") {
      setRecords(
        data.sort((a, b) =>
          sortOrder === "asc"
            ? a.hasCredentials === b.hasCredentials
              ? 0
              : a.hasCredentials
              ? -1
              : 1
            : b.hasCredentials === a.hasCredentials
            ? 0
            : b.hasCredentials
            ? -1
            : 1
        )
      );
    }

    if (sort === "documentsPending") {
      const getFilteredCount = (partner: PartnerElement) => {
        const documentsToExclude = [
          "Pruebas psicométricas aplicadas por reclutamiento",
          "Pruebas psicométricas",
          "Alta en el IMSS (alta ingresada por contabilidad, no se visualiza este rubro)",
          "IMSS",
          "Constancia de semanas cotizadas en el IMSS",
        ];

        const filteredInmediate =
          partner.documents.missingDocuments.missingDocumentsInmediate.filter(
            (file) =>
              !documentsToExclude.some((excluded) =>
                file.name.toLowerCase().includes(excluded.toLowerCase())
              )
          );

        const filteredNoInmediate =
          partner.documents.missingDocuments.missingDocumentsNoInmediate.filter(
            (file) =>
              !documentsToExclude.some((excluded) =>
                file.name.toLowerCase().includes(excluded.toLowerCase())
              )
          );

        return filteredInmediate.length + filteredNoInmediate.length;
      };

      setRecords(
        data.sort((a, b) =>
          sortOrder === "asc"
            ? getFilteredCount(a) - getFilteredCount(b)
            : getFilteredCount(b) - getFilteredCount(a)
        )
      );
    }
  };

  const getHeaderCellStyle = (
    columnName: string,
    isClickable: boolean = true
  ) => ({
    ...styles.headerCell,
    cursor: isClickable ? "pointer" : "default",
    background:
      column === columnName
        ? "rgba(255, 255, 255, 0.2)"
        : hoveredColumn === columnName
        ? "rgba(255, 255, 255, 0.1)"
        : "transparent",
  });

  return (
    <StyledTableContainer>
      <Table aria-label="collapsible table" stickyHeader>
        <StyledTableHead>
          <TableRow>
            <TableCell style={{ ...styles.headerCell, width: "50px" }} />

            <TableCell
              onClick={() => setSort("name")}
              onMouseEnter={() => setHoveredColumn("name")}
              onMouseLeave={() => setHoveredColumn(null)}
              style={getHeaderCellStyle("name")}
            >
              Nombre {column === "name" && getIconSort()}
            </TableCell>

            <TableCell
              onClick={() => setSort("position")}
              onMouseEnter={() => setHoveredColumn("position")}
              onMouseLeave={() => setHoveredColumn(null)}
              style={getHeaderCellStyle("position")}
            >
              Puesto {column === "position" && getIconSort()}
            </TableCell>

            <TableCell
              onClick={() => setSort("recruitmentDate")}
              onMouseEnter={() => setHoveredColumn("recruitmentDate")}
              onMouseLeave={() => setHoveredColumn(null)}
              style={getHeaderCellStyle("recruitmentDate")}
            >
              Fecha de entrada {column === "recruitmentDate" && getIconSort()}
            </TableCell>

            <TableCell
              onClick={() => setSort("credentials")}
              onMouseEnter={() => setHoveredColumn("credentials")}
              onMouseLeave={() => setHoveredColumn(null)}
              align="center"
              style={getHeaderCellStyle("credentials")}
            >
              Credenciales {column === "credentials" && getIconSort()}
            </TableCell>

            <TableCell
              onClick={() => setSort("documentsPending")}
              onMouseEnter={() => setHoveredColumn("documentsPending")}
              onMouseLeave={() => setHoveredColumn(null)}
              align="center"
              style={getHeaderCellStyle("documentsPending")}
            >
              Documentos pendientes{" "}
              {column === "documentsPending" && getIconSort()}
            </TableCell>

            <TableCell
              align="center"
              style={{ ...styles.headerCell, cursor: "default" }}
            >
              Acciones
            </TableCell>
          </TableRow>
        </StyledTableHead>

        <StyledTableBody>
          {records.map((row, index) => (
            <RowPartnetDocuments key={index} data={row} />
          ))}
        </StyledTableBody>
      </Table>
    </StyledTableContainer>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  headerCell: {
    position: "relative",
    transition: "all 0.2s ease",
  },
  sortIconWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    background: "rgba(165, 243, 252, 0.2)",
    marginLeft: "6px",
  },
  sortIcon: {
    color: "#A5F3FC",
    fontSize: "11px",
    fontWeight: "bold",
  },
};

export default TableDocuments;
